import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  UploadFile as UploadFileIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ListPaginationBar from "../components/ListPaginationBar";
import { DEFAULT_PAGE_SIZE, parsePaginatedResponse } from "../constants/pagination";
import { useRequirePermission } from "../hooks/useHasPermission";
import { employeesAPI } from "../services/api";
import { PERMISSIONS } from "../utils/permissions";

const emptyForm = {
  employee_no: "",
  name_ar: "",
  name_en: "",
  is_active: true,
};

const EmployeesPage = ({ onNavigate }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const hasAccess = useRequirePermission(PERMISSIONS.MANAGE_EMPLOYEES, onNavigate);

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [confirmImportOpen, setConfirmImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState("");
  const [importErrors, setImportErrors] = useState([]);
  const [importSuccess, setImportSuccess] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const [existingNos, setExistingNos] = useState([]);
  const fileInputRef = useRef(null);

  const loadItems = useCallback(
    async (pageToLoad = page, sizeToLoad = pageSize, query = appliedSearch) => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await employeesAPI.list({
          page: pageToLoad,
          page_size: sizeToLoad,
          q: query,
        });
        const { items: listItems, totalCount: count } =
          parsePaginatedResponse(response);
        setItems(listItems);
        setTotalCount(count);
      } catch (err) {
        setError(err.message || t("referencePages.loadError"));
      } finally {
        setIsLoading(false);
      }
    },
    [page, pageSize, appliedSearch, t]
  );

  useEffect(() => {
    loadItems(page, pageSize, appliedSearch);
  }, [page, pageSize, appliedSearch, loadItems]);

  const openAdd = () => {
    setSelected(null);
    setFormData(emptyForm);
    setFormErrors({});
    setFormOpen(true);
  };

  const openEdit = (item) => {
    setSelected(item);
    setFormData({
      employee_no: String(item.employee_no),
      name_ar: item.name_ar || "",
      name_en: item.name_en || "",
      is_active: item.is_active !== false,
    });
    setFormErrors({});
    setFormOpen(true);
  };

  const validate = () => {
    const errors = {};
    if (!selected) {
      if (!/^[1-9][0-9]*$/.test(String(formData.employee_no).trim())) {
        errors.employee_no = t("employees.invalidNumber");
      }
    }
    if (!formData.name_ar.trim()) {
      errors.name_ar = t("referencePages.fieldRequired");
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      if (selected) {
        await employeesAPI.update(selected.employee_no, {
          name_ar: formData.name_ar.trim(),
          name_en: formData.name_en.trim(),
          is_active: formData.is_active,
        });
      } else {
        await employeesAPI.create({
          employee_no: Number(formData.employee_no),
          name_ar: formData.name_ar.trim(),
          name_en: formData.name_en.trim(),
          is_active: true,
        });
      }
      setFormOpen(false);
      await loadItems(page, pageSize, appliedSearch);
    } catch (err) {
      setFormErrors({ general: err.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    setIsSubmitting(true);
    try {
      await employeesAPI.delete(selected.employee_no);
      setDeleteOpen(false);
      setSelected(null);
      await loadItems(page, pageSize, appliedSearch);
    } catch (err) {
      setError(err.message);
      setDeleteOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatEmployeeNos = (numbers) => {
    if (!numbers.length) return "";
    if (isRTL) {
      return numbers.join(" و ");
    }
    if (numbers.length === 1) return String(numbers[0]);
    if (numbers.length === 2) return `${numbers[0]} and ${numbers[1]}`;
    return `${numbers.slice(0, -1).join(", ")}, and ${numbers[numbers.length - 1]}`;
  };

  const runImport = async (file, confirmUpdates = false) => {
    setImporting(true);
    setImportError("");
    setImportErrors([]);
    try {
      const result = await employeesAPI.importFile(file, { confirmUpdates });
      setImportSuccess(
        t("employees.importSuccess", {
          created: result.created || 0,
          updated: result.updated || 0,
        })
      );
      setImportOpen(false);
      setConfirmImportOpen(false);
      setPendingFile(null);
      setExistingNos([]);
      setPage(1);
      await loadItems(1, pageSize, appliedSearch);
    } catch (err) {
      if (err.code === "existing_employees" || err.status === 409) {
        const nos = err.data?.employee_nos || [];
        setExistingNos(nos);
        setPendingFile(file);
        setConfirmImportOpen(true);
      } else {
        setImportError(err.message || t("employees.importError"));
        setImportErrors(err.data?.errors || []);
        setConfirmImportOpen(false);
      }
    } finally {
      setImporting(false);
    }
  };

  const handleImportFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    runImport(file, false);
  };

  if (!hasAccess) {
    return null;
  }

  return (
    <Box dir={isRTL ? "rtl" : "ltr"} sx={{ p: { xs: 0.5, sm: 1, md: 1.5 }, width: "100%" }}>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          <Typography variant="h4" component="h1" sx={{ fontWeight: "bold" }}>
            {t("sidebar.employees")}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => {
                setImportError("");
                setImportErrors([]);
                setImportOpen(true);
              }}
            >
              {t("employees.import")}
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
              {t("referencePages.addNew")}
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <TextField
            fullWidth
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                setPage(1);
                setAppliedSearch(search.trim());
              }
            }}
            placeholder={t("employees.searchPlaceholder")}
          />
          <Button
            variant="outlined"
            onClick={() => {
              setPage(1);
              setAppliedSearch(search.trim());
            }}
          >
            {t("common.search")}
          </Button>
        </Box>

        {importSuccess && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setImportSuccess("")}>
            {importSuccess}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold" }}>{t("employees.employeeNo")}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{t("referencePages.nameAr")}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>{t("referencePages.nameEn")}</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }} align="center">
                    {t("common.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      {t("referencePages.noRecords")}
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => (
                    <TableRow key={item.employee_no} hover>
                      <TableCell>{item.employee_no}</TableCell>
                      <TableCell>{item.name_ar}</TableCell>
                      <TableCell>{item.name_en || "—"}</TableCell>
                      <TableCell align="center">
                        <IconButton color="primary" onClick={() => openEdit(item)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => {
                            setSelected(item);
                            setDeleteOpen(true);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!isLoading && (
          <ListPaginationBar
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(1);
            }}
            isRTL={isRTL}
          />
        )}
      </Paper>

      <Dialog
        open={formOpen}
        onClose={() => !isSubmitting && setFormOpen(false)}
        maxWidth="sm"
        fullWidth
        dir={isRTL ? "rtl" : "ltr"}
      >
        <DialogTitle>
          {selected ? t("referencePages.editRecord") : t("referencePages.addNew")}
        </DialogTitle>
        <DialogContent>
          {formErrors.general && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formErrors.general}
            </Alert>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label={t("employees.employeeNo")}
              value={formData.employee_no}
              onChange={(event) =>
                setFormData((prev) => ({
                  ...prev,
                  employee_no: event.target.value.replace(/[^0-9]/g, ""),
                }))
              }
              required
              disabled={Boolean(selected)}
              error={Boolean(formErrors.employee_no)}
              helperText={formErrors.employee_no || t("employees.employeeNoHint")}
              inputProps={{ inputMode: "numeric", pattern: "[1-9][0-9]*" }}
            />
            <TextField
              label={t("referencePages.nameAr")}
              value={formData.name_ar}
              onChange={(event) =>
                setFormData((prev) => ({ ...prev, name_ar: event.target.value }))
              }
              required
              error={Boolean(formErrors.name_ar)}
              helperText={formErrors.name_ar}
            />
            <TextField
              label={t("referencePages.nameEn")}
              value={formData.name_en}
              onChange={(event) =>
                setFormData((prev) => ({ ...prev, name_en: event.target.value }))
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)} disabled={isSubmitting}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={isSubmitting}>
            {isSubmitting ? <CircularProgress size={24} /> : t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} dir={isRTL ? "rtl" : "ltr"}>
        <DialogTitle>{t("referencePages.confirmDelete")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("referencePages.deleteConfirmMessage", {
              name: selected ? `${selected.employee_no} - ${selected.name_ar}` : "",
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>{t("common.cancel")}</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={isSubmitting}
          >
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={importOpen}
        onClose={() => !importing && setImportOpen(false)}
        maxWidth="sm"
        fullWidth
        dir={isRTL ? "rtl" : "ltr"}
      >
        <DialogTitle>{t("employees.importTitle")}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>{t("employees.importHint")}</Typography>
          {importError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {importError}
            </Alert>
          )}
          {importErrors.length > 0 && (
            <Box component="ul" sx={{ mt: 0, mb: 2, ps: 3 }}>
              {importErrors.map((item, index) => (
                <Typography component="li" key={`${item.row}-${index}`} variant="body2">
                  {t("employees.importRowError", {
                    row: item.row,
                    message: item.message,
                  })}
                </Typography>
              ))}
            </Box>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            hidden
            onChange={handleImportFile}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            disabled={importing}
            onClick={() => employeesAPI.downloadImportTemplate(i18n.language)}
          >
            {t("employees.downloadTemplate")}
          </Button>
          <Button
            variant="contained"
            startIcon={<UploadFileIcon />}
            disabled={importing}
            onClick={() => fileInputRef.current?.click()}
          >
            {importing ? <CircularProgress size={22} /> : t("employees.importFile")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmImportOpen}
        onClose={() => !importing && setConfirmImportOpen(false)}
        dir={isRTL ? "rtl" : "ltr"}
      >
        <DialogTitle>{t("employees.importConfirmTitle")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("employees.importExistingConfirm", {
              numbers: formatEmployeeNos(existingNos),
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmImportOpen(false);
              setPendingFile(null);
              setExistingNos([]);
            }}
            disabled={importing}
          >
            {t("referencePages.no")}
          </Button>
          <Button
            variant="contained"
            disabled={importing || !pendingFile}
            onClick={() => pendingFile && runImport(pendingFile, true)}
          >
            {importing ? <CircularProgress size={22} /> : t("referencePages.yes")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeesPage;
